import {
  collection,
  doc,
  getDoc,
  getDocs,
  Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  CreatePurchaseReceiptInput,
  NormalizedPurchaseReceiptInput,
  NormalizedPurchaseReceiptItemInput,
  PurchaseReceipt,
  PurchaseReceiptItem,
  PurchaseReceiptItemInput,
  PurchaseReceiptStatus,
  PurchaseReceiptWithItems,
} from '../types/purchaseReceipt';

export const PURCHASE_RECEIPTS_COLLECTION = 'purchase-receipts';
export const PURCHASE_RECEIPT_ITEMS_COLLECTION = 'items';

const toDateValue = (value: unknown, field: string, nullable = false): Date | null => {
  if (value === null || value === undefined || value === '') {
    if (nullable) return null;
    throw new Error(`${field} must be a valid date`);
  }

  const result =
    value instanceof Date
      ? new Date(value.getTime())
      : typeof (value as { toDate?: unknown })?.toDate === 'function'
        ? new Date((value as { toDate: () => Date }).toDate().getTime())
        : new Date(String(value));
  if (Number.isNaN(result.getTime())) throw new Error(`${field} must be a valid date`);
  return result;
};

const normalizeText = (value: unknown, field: string, required = false): string | null => {
  if (value === null || value === undefined) {
    if (required) throw new Error(`${field} is required`);
    return null;
  }
  if (typeof value !== 'string') throw new Error(`${field} must be a string`);

  const result = value.trim();
  if (!result && required) throw new Error(`${field} is required`);
  return result || null;
};

const normalizeNumber = (value: unknown, field: string, minimum: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${field} must be a finite number`);
  }
  if (value < minimum) throw new Error(`${field} must be ${minimum === 0 ? 'non-negative' : 'greater than 0'}`);
  return value;
};

const normalizeStatus = (value: unknown): PurchaseReceiptStatus => {
  if (value === undefined) return 'draft';
  if (value !== 'draft' && value !== 'confirmed' && value !== 'cancelled') {
    throw new Error('status is invalid');
  }
  return value;
};

export const normalizePurchaseReceiptInput = (
  input: CreatePurchaseReceiptInput,
): NormalizedPurchaseReceiptInput => {
  if (!Array.isArray(input.items) || input.items.length === 0) {
    throw new Error('Receipt must contain at least one item');
  }

  const items: NormalizedPurchaseReceiptItemInput[] = input.items.map((item, index) => {
    const materialId = normalizeText(item.materialId, `items[${index}].materialId`, true) as string;
    const unit = normalizeText(item.unit, `items[${index}].unit`, true) as string;
    const quantity = normalizeNumber(item.quantity, `items[${index}].quantity`, Number.MIN_VALUE);
    const unitPrice = normalizeNumber(item.unitPrice, `items[${index}].unitPrice`, 0);
    const total =
      item.total === undefined
        ? quantity * unitPrice
        : normalizeNumber(item.total, `items[${index}].total`, 0);
    if (!Number.isFinite(total)) {
      throw new Error(`items[${index}].total must be a finite number`);
    }

    return {
      materialId,
      quantity,
      unit,
      unitPrice,
      total,
      expiryDate: toDateValue(item.expiryDate, `items[${index}].expiryDate`, true),
      note: normalizeText(item.note, `items[${index}].note`),
      inventoryQuantity:
        item.inventoryQuantity === undefined || item.inventoryQuantity === null
          ? null
          : normalizeNumber(item.inventoryQuantity, `items[${index}].inventoryQuantity`, 0),
      inventoryUnit: normalizeText(item.inventoryUnit, `items[${index}].inventoryUnit`),
      inventoryUnitPrice:
        item.inventoryUnitPrice === undefined || item.inventoryUnitPrice === null
          ? null
          : normalizeNumber(item.inventoryUnitPrice, `items[${index}].inventoryUnitPrice`, 0),
      sourcePurchaseId: normalizeText(item.sourcePurchaseId, `items[${index}].sourcePurchaseId`),
    };
  });

  return {
    code: normalizeText(input.code, 'code', true) as string,
    date: toDateValue(input.date, 'date') as Date,
    storeLocationId: normalizeText(input.storeLocationId, 'storeLocationId', true) as string,
    supplierId: normalizeText(input.supplierId, 'supplierId'),
    supplierName: normalizeText(input.supplierName, 'supplierName'),
    createdBy: normalizeText(input.createdBy, 'createdBy', true) as string,
    note: normalizeText(input.note, 'note'),
    status: normalizeStatus(input.status),
    items,
  };
};

const normalizeReceipt = (id: string, raw: Record<string, any>): PurchaseReceipt => ({
  id,
  code: String(raw.code || '').trim(),
  date: toDateValue(raw.date, 'date') as Date,
  storeLocationId: String(raw.storeLocationId || '').trim(),
  supplierId: normalizeText(raw.supplierId, 'supplierId'),
  supplierName: normalizeText(raw.supplierName, 'supplierName'),
  createdBy: String(raw.createdBy || '').trim(),
  note: normalizeText(raw.note, 'note'),
  status: normalizeStatus(raw.status),
  createdAt: toDateValue(raw.createdAt, 'createdAt') as Date,
  updatedAt: toDateValue(raw.updatedAt, 'updatedAt') as Date,
});

const normalizeReceiptItem = (id: string, receiptId: string, raw: Record<string, any>): PurchaseReceiptItem => ({
  id,
  receiptId,
  materialId: String(raw.materialId || '').trim(),
  quantity: Number(raw.quantity),
  unit: String(raw.unit || '').trim(),
  unitPrice: Number(raw.unitPrice),
  total: Number(raw.total),
  expiryDate: toDateValue(raw.expiryDate, 'expiryDate', true),
  note: normalizeText(raw.note, 'note'),
  inventoryQuantity: raw.inventoryQuantity === undefined ? null : Number(raw.inventoryQuantity),
  inventoryUnit: normalizeText(raw.inventoryUnit, 'inventoryUnit'),
  inventoryUnitPrice: raw.inventoryUnitPrice === undefined ? null : Number(raw.inventoryUnitPrice),
  sourcePurchaseId: normalizeText(raw.sourcePurchaseId, 'sourcePurchaseId'),
  createdAt: toDateValue(raw.createdAt, 'createdAt') as Date,
  updatedAt: toDateValue(raw.updatedAt, 'updatedAt') as Date,
});

const toFirestoreItem = (item: NormalizedPurchaseReceiptItemInput, receiptId: string, timestamp: Timestamp) => ({
  receiptId,
  materialId: item.materialId,
  quantity: item.quantity,
  unit: item.unit,
  unitPrice: item.unitPrice,
  total: item.total,
  expiryDate: item.expiryDate ? Timestamp.fromDate(item.expiryDate) : null,
  note: item.note,
  inventoryQuantity: item.inventoryQuantity,
  inventoryUnit: item.inventoryUnit,
  inventoryUnitPrice: item.inventoryUnitPrice,
  sourcePurchaseId: item.sourcePurchaseId,
  createdAt: timestamp,
  updatedAt: timestamp,
});

export const purchaseReceiptService = {
  async createReceipt(input: CreatePurchaseReceiptInput): Promise<PurchaseReceiptWithItems> {
    const normalized = normalizePurchaseReceiptInput(input);
    const receiptRef = doc(collection(db, PURCHASE_RECEIPTS_COLLECTION));
    const timestamp = Timestamp.fromDate(new Date());
    const batch = writeBatch(db);

    batch.set(receiptRef, {
      code: normalized.code,
      date: Timestamp.fromDate(normalized.date),
      storeLocationId: normalized.storeLocationId,
      supplierId: normalized.supplierId,
      supplierName: normalized.supplierName,
      createdBy: normalized.createdBy,
      note: normalized.note,
      status: normalized.status,
      createdAt: timestamp,
      updatedAt: timestamp,
    });

    const items: PurchaseReceiptItem[] = normalized.items.map((item) => {
      const itemRef = doc(
        collection(db, PURCHASE_RECEIPTS_COLLECTION, receiptRef.id, PURCHASE_RECEIPT_ITEMS_COLLECTION),
      );
      batch.set(itemRef, toFirestoreItem(item, receiptRef.id, timestamp));

      return {
        ...item,
        id: itemRef.id,
        receiptId: receiptRef.id,
        createdAt: timestamp.toDate(),
        updatedAt: timestamp.toDate(),
      };
    });

    await batch.commit();

    return {
      receipt: {
        id: receiptRef.id,
        ...normalized,
        createdAt: timestamp.toDate(),
        updatedAt: timestamp.toDate(),
      },
      items,
    };
  },

  async getReceiptById(id: string): Promise<PurchaseReceiptWithItems | null> {
    const receiptId = id.trim();
    if (!receiptId) throw new Error('Receipt id is required');

    const receiptSnapshot = await getDoc(doc(db, PURCHASE_RECEIPTS_COLLECTION, receiptId));
    if (!receiptSnapshot.exists()) return null;

    const itemSnapshot = await getDocs(
      collection(db, PURCHASE_RECEIPTS_COLLECTION, receiptId, PURCHASE_RECEIPT_ITEMS_COLLECTION),
    );

    return {
      receipt: normalizeReceipt(receiptSnapshot.id, receiptSnapshot.data()),
      items: itemSnapshot.docs.map((item) => normalizeReceiptItem(item.id, receiptId, item.data())),
    };
  },

  async getReceipts(): Promise<PurchaseReceipt[]> {
    const snapshot = await getDocs(collection(db, PURCHASE_RECEIPTS_COLLECTION));
    return snapshot.docs
      .map((item) => normalizeReceipt(item.id, item.data()))
      .sort((a, b) => b.date.getTime() - a.date.getTime());
  },

  async getImportedLegacyPurchaseIds(): Promise<Set<string>> {
    const receiptSnapshot = await getDocs(collection(db, PURCHASE_RECEIPTS_COLLECTION));
    const sourceIds = new Set<string>();

    for (const receipt of receiptSnapshot.docs) {
      const itemSnapshot = await getDocs(
        collection(db, PURCHASE_RECEIPTS_COLLECTION, receipt.id, PURCHASE_RECEIPT_ITEMS_COLLECTION),
      );
      itemSnapshot.docs.forEach((item) => {
        const sourcePurchaseId = item.data().sourcePurchaseId;
        if (typeof sourcePurchaseId !== 'string') return;
        const normalized = sourcePurchaseId.trim();
        if (normalized) sourceIds.add(normalized);
      });
    }

    return sourceIds;
  },

  /**
   * Returns the existing receipt for every legacy purchase line already
   * attached to a receipt. The full receipt is needed when backfilling a
   * supplier without creating a second receipt.
   */
  async getImportedLegacyPurchaseReceipts(): Promise<Map<string, PurchaseReceiptWithItems[]>> {
    const receiptRows = await purchaseReceiptService.getReceipts();
    const bySourcePurchaseId = new Map<string, PurchaseReceiptWithItems[]>();

    for (const receipt of receiptRows) {
      if (!receipt.id) continue;
      const details = await purchaseReceiptService.getReceiptById(receipt.id);
      if (!details) continue;
      details.items.forEach((item) => {
        const sourcePurchaseId = item.sourcePurchaseId?.trim();
        if (!sourcePurchaseId) return;
        const matches = bySourcePurchaseId.get(sourcePurchaseId) || [];
        if (!matches.some((match) => match.receipt.id === details.receipt.id)) matches.push(details);
        bySourcePurchaseId.set(sourcePurchaseId, matches);
      });
    }

    return bySourcePurchaseId;
  },

  async updateReceipt(
    id: string,
    input: Partial<Pick<PurchaseReceipt, 'code' | 'storeLocationId' | 'supplierId' | 'supplierName' | 'createdBy' | 'note' | 'status'>> & {
      date?: Date | string;
    },
  ): Promise<void> {
    const receiptId = id.trim();
    if (!receiptId) throw new Error('Receipt id is required');
    const payload: Record<string, any> = { updatedAt: Timestamp.fromDate(new Date()) };

    if (input.code !== undefined) payload.code = normalizeText(input.code, 'code', true);
    if (input.storeLocationId !== undefined) {
      payload.storeLocationId = normalizeText(input.storeLocationId, 'storeLocationId', true);
    }
    if (input.supplierId !== undefined) payload.supplierId = normalizeText(input.supplierId, 'supplierId');
    if (input.supplierName !== undefined) {
      payload.supplierName = normalizeText(input.supplierName, 'supplierName');
    }
    if (input.createdBy !== undefined) payload.createdBy = normalizeText(input.createdBy, 'createdBy', true);
    if (input.note !== undefined) payload.note = normalizeText(input.note, 'note');
    if (input.status !== undefined) payload.status = normalizeStatus(input.status);
    if (input.date !== undefined) payload.date = Timestamp.fromDate(toDateValue(input.date, 'date') as Date);

    if (Object.keys(payload).length === 1) throw new Error('No receipt fields to update');
    await updateDoc(doc(db, PURCHASE_RECEIPTS_COLLECTION, receiptId), payload);
  },

  async updateReceiptWithItems(
    id: string,
    input: Partial<Pick<PurchaseReceipt, 'code' | 'storeLocationId' | 'supplierId' | 'supplierName' | 'createdBy' | 'note' | 'status'>> & {
      date?: Date | string;
      items: PurchaseReceiptItemInput[];
    },
  ): Promise<void> {
    const receiptId = id.trim();
    if (!receiptId) throw new Error('Receipt id is required');

    const existing = await purchaseReceiptService.getReceiptById(receiptId);
    if (!existing) throw new Error('Receipt not found');

    const normalized = normalizePurchaseReceiptInput({
      code: input.code ?? existing.receipt.code,
      date: input.date ?? existing.receipt.date,
      storeLocationId: input.storeLocationId ?? existing.receipt.storeLocationId,
      supplierId: input.supplierId ?? existing.receipt.supplierId,
      supplierName: input.supplierName ?? existing.receipt.supplierName,
      createdBy: input.createdBy ?? existing.receipt.createdBy,
      note: input.note ?? existing.receipt.note,
      status: input.status ?? existing.receipt.status,
      items: input.items,
    });

    const timestamp = Timestamp.fromDate(new Date());
    const receiptRef = doc(db, PURCHASE_RECEIPTS_COLLECTION, receiptId);
    const itemSnapshot = await getDocs(
      collection(db, PURCHASE_RECEIPTS_COLLECTION, receiptId, PURCHASE_RECEIPT_ITEMS_COLLECTION),
    );
    const batch = writeBatch(db);
    batch.update(receiptRef, {
      code: normalized.code,
      date: Timestamp.fromDate(normalized.date),
      storeLocationId: normalized.storeLocationId,
      supplierId: normalized.supplierId,
      supplierName: normalized.supplierName,
      createdBy: normalized.createdBy,
      note: normalized.note,
      status: normalized.status,
      updatedAt: timestamp,
    });
    itemSnapshot.docs.forEach((item) => batch.delete(doc(
      db,
      PURCHASE_RECEIPTS_COLLECTION,
      receiptId,
      PURCHASE_RECEIPT_ITEMS_COLLECTION,
      item.id,
    )));

    normalized.items.forEach((item) => {
      const itemRef = doc(
        collection(db, PURCHASE_RECEIPTS_COLLECTION, receiptId, PURCHASE_RECEIPT_ITEMS_COLLECTION),
      );
      batch.set(itemRef, toFirestoreItem(item, receiptId, timestamp));
    });
    await batch.commit();
  },

  async deleteReceipt(id: string): Promise<void> {
    const receiptId = id.trim();
    if (!receiptId) throw new Error('Receipt id is required');
    const receiptRef = doc(db, PURCHASE_RECEIPTS_COLLECTION, receiptId);
    const itemSnapshot = await getDocs(
      collection(db, PURCHASE_RECEIPTS_COLLECTION, receiptId, PURCHASE_RECEIPT_ITEMS_COLLECTION),
    );
    const batch = writeBatch(db);
    itemSnapshot.docs.forEach((item) => batch.delete(item.ref));
    batch.delete(receiptRef);
    await batch.commit();
  },

  /** Deletes selected receipts and their item subcollections one by one. */
  async deleteReceipts(ids: string[]): Promise<number> {
    if (!Array.isArray(ids)) throw new Error('Receipt ids must be an array');
    const receiptIds = Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean)));
    if (receiptIds.length > 500) throw new Error('Chá»‰ cÃ³ thá»ƒ xÃ³a tá»‘i Ä‘a 500 phiáº¿u má»—i láº§n');

    for (const receiptId of receiptIds) {
      await purchaseReceiptService.deleteReceipt(receiptId);
    }
    return receiptIds.length;
  },
};
